import { motion } from "@/lib/motion";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.webp";

const values = [
  "No hidden fees or unnecessary upsells",
  "Transparent pricing and processes",
  "Direct communication, always",
  "Results-focused partnerships"
];

export const About = () => {
  return (
    <section id="about" className="py-24 relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-1/2 right-0 w-1/2 h-96 bg-primary/5 blur-3xl rounded-full -translate-y-1/2" />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">
              About Us
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-6">
              We help you <span className="text-gradient">succeed</span>
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">
              I know trust isn't given easily, and for good reason. Too many people promise the moon, only to deliver excuses and headaches. It's exhausting.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              But here's the difference—I don't hide behind fancy words or overinflated claims. What you see is what you get: someone who's honest, direct, and fully committed to delivering real results.
            </p>
            
            {/* Values List */}
            <ul className="space-y-4 mb-8">
              {values.map((value, index) => (
                <motion.li
                  key={value}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-foreground">{value}</span>
                </motion.li>
              ))}
            </ul>

            <Link to="/about">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 glow-sm">
                Learn More About Us
              </Button>
            </Link>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative aspect-square max-w-md mx-auto">
              {/* Decorative rings */}
              <div className="absolute inset-0 border border-border/30 rounded-full" />
              <div className="absolute inset-8 border border-border/40 rounded-full" />
              <div className="absolute inset-16 border border-primary/30 rounded-full" />
              
              {/* Center content */}
              <div className="absolute inset-24 glass rounded-full flex items-center justify-center p-8">
                <img 
                  src={logo} 
                  alt="Solved Media" 
                  className="w-full h-auto max-w-[120px]"
                  width={120}
                  height={113}
                  loading="lazy"
                />
              </div>

              {/* Floating elements */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0"
              >
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 bg-primary rounded-full glow-sm" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-primary/60 rounded-full" />
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-primary/40 rounded-full" />
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-primary/80 rounded-full" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
